// ABOUTME: Landing page after the payment service: reports the payment's outcome and returns to the payment settings.
// ABOUTME: Back with an upgrade's payment still unsettled, the payment settings check on that upgrade for a while.
import { useParams } from "react-router"
import { useEffect } from "react"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { useQuery } from "@apollo/client/react"
import { CSpinner } from "@shared/ui"
import {
  PaymentStatus,
  ProductType,
  PaymentListForBuyerDocument,
  SubscriptionListDocument,
} from "@/graphql/main/gql/graphql"
import { safeNavigate } from "@/shared/utils/urlValidation"
import { addLogError } from "@/model/logger"
import { rememberReturnedCheckout } from "@/features/settings/hooks/payment"

const PaymentStatusPage = () => {
  const params = useParams()
  const { t } = useTranslation()
  const { handleNotification } = useNotificationContext()
  const navigate = useNavigate()
  // paging: null = server default (newest 50) — the payment being checked has
  // just been made, so it is always within the newest page.
  const { data: dataPaymentForBuy, loading: _loading } = useQuery(PaymentListForBuyerDocument, {
    variables: { paging: null },
  })
  const { data: dataSubscriptionList } = useQuery(SubscriptionListDocument, {
    variables: { filter: { statuses: [] } },
  })

  useEffect(() => {
    if (!params.id) return
    if (!dataPaymentForBuy) return
    if (!dataSubscriptionList) return

    const paymentItems = dataPaymentForBuy.paymentListForBuyer.items
    const subscriptionItems = dataSubscriptionList.subscriptionList.items

    const targetPay = paymentItems.find((item) => item.id === params.id)
    // Coming back says nothing of the outcome: YooKassa sends everyone to the success URL, paid or not
    // (payment-gateway yookassa client_create_payment: ReturnURL = SuccessURL). While the payment is unsettled, its
    // webhook may just be late, so the upgrade its link was for is checked on for a while rather than offered for
    // replacement. The link names that very upgrade: gql-api CreateUpgradePayment stores the order URL it hands
    // out as the payment's provider info.
    const unsettledLink =
      params.status === "success" && targetPay?.status === PaymentStatus.Pending
        ? targetPay.paymentInfoFromPaymentProvider
        : null
    if (unsettledLink) rememberReturnedCheckout(unsettledLink)
    if (targetPay) {
      const targetSubscription = subscriptionItems.find((item) => item.product?.id === targetPay.items[0]?.productID)
      const isTrainerLicense = targetSubscription?.product?.type === ProductType.SystemCoach

      if (targetPay.status === PaymentStatus.Paid) {
        handleNotification("success", t("common.paymentSuccesfull"), true, 3000)
        if (isTrainerLicense) {
          const navigationSuccess = safeNavigate("/settings/payment?showTrainerModal=true", navigate)
          if (!navigationSuccess) {
            addLogError("Navigation blocked for trainer license redirect")
            // Fallback - навигация без параметра
            safeNavigate("/settings/payment", navigate)
          }
          return
        }
      } else if (targetPay.status === PaymentStatus.Failed) {
        handleNotification("error", t("common.paymentFailure"), true, 3000)
      } else if (targetPay.status === PaymentStatus.Cancelled) {
        handleNotification("error", t("common.paymentCancelled"), true, 3000)
      } else if (targetPay.status === PaymentStatus.Pending) {
        handleNotification("error", t("common.paymentProcessed"), true, 3000)
      } else if (targetPay.status === PaymentStatus.Refunded) {
        handleNotification("error", t("common.paymentRefunded"), true, 3000)
      } else {
        handleNotification("error", t("common.paymentNotFoundStatus"), true, 3000)
      }
    }

    // Without the payment there is no outcome to report: the success URL is no word of success, YooKassa sends
    // everyone back to it (see above).
    if (!targetPay) {
      if (params.status === "failure") {
        handleNotification("error", t("common.paymentFailure"), true, 3000)
      } else {
        handleNotification("error", t("common.paymentNotFoundStatus"), true, 3000)
      }
    }

    // Безопасная навигация к странице настроек
    const navigationSuccess = safeNavigate("/settings/payment", navigate)
    if (!navigationSuccess) {
      addLogError("Navigation to /settings/payment blocked")
      // Fallback - попробуем основную страницу настроек
      safeNavigate("/settings/profile", navigate)
    }
  }, [params.id, dataPaymentForBuy, dataSubscriptionList, params.status, navigate, handleNotification, t])

  return <CSpinner typeHeight="screen" />
}

export default PaymentStatusPage
